import type { ViewerCameraOptions } from "@workspace/blocks";
import { Button } from "@workspace/blocks/ui";
import { AlertCircle, RotateCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "../i18n";
import { ViewerLoadingState } from "./viewer/ViewerLoadingState";

type ViewerModule = typeof import("@workspace/blocks").Viewer;
export type GaussianViewerProps = ViewerCameraOptions & {
	src: string;
	previewMode?: boolean;
	onError?: (error: Error) => void;
	onAssetProgress?: (progress: number) => void;
};

/** One loading/error/toolbar contract for the PLY/SOG and SPZ renderers. */
export function GaussianViewer({
	format,
	src,
	previewMode = false,
	onError,
	...camera
}: GaussianViewerProps & { format: "ply" | "spz" }) {
	const { t } = useTranslation();
	const [viewer, setViewer] = useState<ViewerModule | null>(null);
	const [error, setError] = useState<Error | null>(null);
	const [attempt, setAttempt] = useState(0);
	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;
	useEffect(() => {
		let active = true;
		setError(null);
		const load = format === "spz" ? import("@workspace/spz") : import("@workspace/blocks");
		load
			.then(module => {
				if (active) setViewer(module.Viewer as ViewerModule);
			})
			.catch(cause => {
				if (!active) return;
				const failure = cause instanceof Error ? cause : new Error(String(cause));
				setError(failure);
				onErrorRef.current?.(failure);
			});
		return () => {
			active = false;
		};
	}, [format, attempt, src]);
	const handleError = useCallback((failure: Error) => {
		setError(failure);
		onErrorRef.current?.(failure);
	}, []);
	if (error)
		return (
			<div className="flex h-full min-h-48 items-center justify-center p-6">
				<div role="alert" className="max-w-sm space-y-4 text-center">
					<AlertCircle className="mx-auto size-8 text-destructive" />
					<div>
						<p className="font-medium text-foreground">{t("viewer.renderError")}</p>
						<p className="mt-2 break-words text-sm text-muted-foreground">{error.message}</p>
					</div>
					<Button
						variant="outline"
						onClick={() => {
							setError(null);
							setAttempt(value => value + 1);
						}}
					>
						<RotateCw />
						{t("viewer.refresh")}
					</Button>
				</div>
			</div>
		);
	if (!viewer)
		return (
			<div className="relative h-full min-h-48">
				<ViewerLoadingState />
			</div>
		);
	const {
		Splat,
		Controls,
		FullScreenButton,
		CameraModeToggle,
		HelpButton,
		ResetCameraButton,
		ViewerSettingsButton,
		ViewerSettingsDialog,
		WalkButton,
		WalkOverlay,
	} = viewer;
	return (
		<Splat key={`${format}:${src}:${attempt}`} src={src} previewMode={previewMode} onError={handleError} {...camera}>
			{!previewMode && (
				<Controls>
					<CameraModeToggle />
					<WalkButton />
					<ResetCameraButton />
					<ViewerSettingsButton />
					<FullScreenButton />
					<HelpButton />
				</Controls>
			)}
			{!previewMode && <ViewerSettingsDialog />}
			{!previewMode && <WalkOverlay />}
		</Splat>
	);
}
